/**
 * Guide chips — the Study Buddy's way of taking a child somewhere useful.
 * The backend reads `[[GO:key]]` markers out of the reply and hands us a small
 * list of {key, label, path}. One tap and the kid is on the right screen.
 */
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpRight, Coins, Compass, FileText, GraduationCap, Image as ImageIcon,
  Gift, Heart, Library, Palette, Puzzle, Sparkles, Trophy, Upload, Zap,
} from 'lucide-react';

const ICONS = {
  flashcards: Zap, quiz: Trophy, puzzle: Puzzle, graphics: ImageIcon, library: Library,
  coins: Coins, themes: Palette, syllabus: Upload, subscription: Sparkles,
  refer: Gift, exams: GraduationCap, gurukul: Compass, safespace: Heart,
};
const TONES = {
  flashcards: 'from-emerald-500 to-teal-500', quiz: 'from-amber-500 to-orange-500',
  puzzle: 'from-fuchsia-500 to-pink-500', graphics: 'from-blue-500 to-indigo-500',
  library: 'from-slate-600 to-slate-700', coins: 'from-amber-400 to-yellow-500',
  themes: 'from-violet-500 to-purple-500', syllabus: 'from-indigo-500 to-violet-500',
  subscription: 'from-rose-500 to-orange-500', refer: 'from-emerald-500 to-green-600',
  exams: 'from-sky-500 to-blue-600', gurukul: 'from-cyan-500 to-sky-600',
  safespace: 'from-rose-400 to-pink-500',
};

export function GuideChips({ guide = [], subject = '', topic = '' }) {
  const navigate = useNavigate();
  if (!guide.length) return null;

  const go = (chip) => {
    if (chip.key === 'safespace') {
      window.dispatchEvent(new Event('gv:open-safespace'));
      return;
    }
    const params = new URLSearchParams();
    if (['flashcards', 'quiz', 'puzzle', 'graphics'].includes(chip.key)) {
      if (subject) params.set('subject', subject);
      if (topic) params.set('topic', topic);
    }
    const qs = params.toString();
    navigate(qs ? `${chip.path}?${qs}` : chip.path);
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-100" data-testid="guide-chips">
      <p className="text-[11px] font-heading text-slate-500 mb-2 flex items-center gap-1.5">
        <ArrowUpRight className="w-3 h-3 text-sky-500" /> I can take you straight there
      </p>
      <div className="flex flex-wrap gap-1.5">
        {guide.map(chip => {
          const Icon = ICONS[chip.key] || FileText;
          return (
            <button key={chip.key} data-testid={`guide-chip-${chip.key}`} onClick={() => go(chip)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-[11px] font-heading bg-gradient-to-r ${TONES[chip.key] || 'from-slate-600 to-slate-700'} hover:scale-105 active:scale-95 transition-transform shadow-sm`}>
              <Icon className="w-3 h-3" />
              {chip.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default GuideChips;
